import { formatISODate, formatTime } from "@clipboard-health/date-time";
import { Text } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { ScrollbarWrapper } from "@src/appV2/redesign/components/ScrollbarWrapper";
import { SelectableBox } from "@src/appV2/redesign/components/SelectableBox";
import { format } from "date-fns";

import { type PlacementDetail } from "../api/useGetPlacement";

export interface InterviewTime {
  start: Date;
  end: Date;
}

interface InterviewTimeSelectorProps {
  isSelectionPromoted: boolean;
  placementDetails: PlacementDetail;
  timeSlots: InterviewTime[];
  selectedTime: InterviewTime | undefined;
  onTimeSelect: (time: InterviewTime) => void;
  isReschedule?: boolean;
}

export function InterviewTimeSelector(props: InterviewTimeSelectorProps) {
  const {
    isSelectionPromoted,
    placementDetails,
    timeSlots,
    selectedTime,
    onTimeSelect,
    isReschedule = false,
  } = props;

  const isProspectWorkplace = Boolean(placementDetails.workplace.placementSettings?.isProspect);

  const eventContext = {
    placementId: placementDetails.id,
    placementApplicationId: placementDetails.applications?.[0]?.id,
    isReschedule,
    isProspectWorkplace,
  };

  const handleTimeSelect = (time: InterviewTime) => {
    onTimeSelect(time);

    // Track time selection
    logEvent(APP_V2_APP_EVENTS.INTERVIEW_TIME_SELECTED, {
      ...eventContext,
      selectedDate: formatISODate(time.start),
      selectedTimeRange: `${formatTime(time.start)} - ${formatTime(time.end)}`,
    });
  };

  return (
    <Stack spacing={1} sx={{ py: 2 }}>
      <Text variant="body1" sx={{ py: 1 }}>
        Available time slots
      </Text>
      <ScrollbarWrapper height="280px" sx={{ p: 2, gap: 3 }} showScrollbarTrack={false}>
        {timeSlots.map((time) => (
          <SelectableBox
            key={time.start.toISOString()}
            variant="secondary"
            sx={{
              ...(isSelectionPromoted && {
                background: `linear-gradient(90.67deg, #FAECDE 5.65%, #D7D4FF 94.45%)`,
              }),
            }}
            isSelected={selectedTime?.start.getTime() === time.start.getTime()}
            onToggleSelect={() => {
              handleTimeSelect(time);
            }}
          >
            {format(time.start, "h:mm aaa")} - {format(time.end, "h:mm aaa")}
          </SelectableBox>
        ))}
      </ScrollbarWrapper>
    </Stack>
  );
}
